import { MODULES, type ModuleId } from '@/lib/modules';
import type { GlobalSearchHit } from '@/types';

/**
 * The URL contract the shell shares with every module.
 *
 * A record opens as `?open=<id>` (see `useListParams`) and a creation intent
 * arrives as `?create=1`, so the command palette can ask any module to open or
 * create without importing a line of that module's code.
 */
export const OPEN_PARAM = 'open';
export const CREATE_PARAM = 'create';

/**
 * Modules whose route still answers with the «estamos construyendo esta pantalla»
 * placeholder.
 *
 * The shell is what promises a destination, so the list lives here and not in the
 * plan: offering six of eleven nav items that dead-end reads as a broken product.
 * The entitlement stays **on**, which is the difference between this and dropping
 * them from the tenant — Actividad, Tareas, Bandeja and Llamadas still power the
 * panel's feed, the contact timeline and its quick actions, and turning them off
 * would delete those instead of fixing anything. An item comes back to the nav the
 * day its module ships a screen, by deleting one line.
 */
/**
 * Modules the shell must not offer as a destination because they have no screen.
 *
 * Empty, and it must stay empty: every module in the registry now ships a real
 * page. This list is a scaffolding hatch for a module added to `modules.ts`
 * before its screen exists — leaving a shipped module in it silently hides it
 * from the sidebar and the command palette while its route still works, which is
 * exactly the split-brain it caused once already.
 */
export const MODULES_WITHOUT_SCREEN: readonly ModuleId[] = [];

/** Whether the shell may offer this module as somewhere to go. */
export const isNavigable = (id: ModuleId): boolean => !MODULES_WITHOUT_SCREEN.includes(id);

const HIT_MODULE: Record<GlobalSearchHit['type'], ModuleId> = {
  lead: 'leads',
  contact: 'contacts',
  company: 'companies',
  deal: 'deals',
  task: 'tasks',
};

export const HIT_LABELS: Record<GlobalSearchHit['type'], string> = {
  lead: 'Lead',
  contact: 'Contacto',
  company: 'Empresa',
  deal: 'Negocio',
  task: 'Tarea',
};

export const hitModule = (type: GlobalSearchHit['type']): ModuleId => HIT_MODULE[type];

export const hitHref = (hit: Pick<GlobalSearchHit, 'type' | 'id'>): string =>
  `${MODULES[HIT_MODULE[hit.type]].path}?${OPEN_PARAM}=${encodeURIComponent(hit.id)}`;

export const createHref = (id: ModuleId): string => `${MODULES[id].path}?${CREATE_PARAM}=1`;

/** The `g` prefix jumps of ARCHITECTURE §9 — the hotkeys and the palette read the same map. */
export const GOTO_SHORTCUTS: Partial<Record<ModuleId, string>> = {
  leads: 'g l',
  deals: 'g d',
  contacts: 'g c',
  tasks: 'g t',
};

const ALL_CREATE_ACTIONS: readonly { id: ModuleId; label: string }[] = [
  { id: 'leads', label: 'Nuevo lead' },
  { id: 'contacts', label: 'Nuevo contacto' },
  { id: 'companies', label: 'Nueva empresa' },
  { id: 'deals', label: 'Nuevo negocio' },
  { id: 'tasks', label: 'Nueva tarea' },
];

/**
 * Modules whose main entity can be created from anywhere, with its article right.
 * Creating something you cannot then look at is worse than not offering it, so a
 * module without a screen drops out here too.
 */
export const CREATE_ACTIONS: readonly { id: ModuleId; label: string }[] =
  ALL_CREATE_ACTIONS.filter((action) => isNavigable(action.id));
